import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { PATHS } from '@/routes/paths';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import tiaTrilhaEscolha from '@/assets/images/tia/TIA-TRILHA-ESCOLHA.png';
import { TypewriterText } from '@/components/custom/typewriter-text';
import { MetricSection, MetricHat } from '@/components/custom/metric';
import { TrilhaCard } from './trilha-card';

export function LearningPathGate() {
  const client = useClient();
  const navigate = useNavigate();
  const playful = usePlayfulStudent();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let mounted = true;
    client.fetch('learning_paths').then(response => {
      if (!mounted) return;
      setPaths(response.data || []);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [client]);

  const start = async learningPathId => {
    setStarting(true);
    const response = await client.mutate('user_learning_paths', { 'learning-path-id': learningPathId }, { method: 'POST' });
    setStarting(false);
    if (response?.data) navigate(PATHS.LEARNING_PATH.CURRENT_BY_ID(response.data.id));
  };

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [{ route: PATHS.LEARNING_PATH.ROOT, name: 'Trilha', isActive: true }]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      {loading && <Loader />}

      {!loading && (
        <div className={playful ? 'w-full' : 'mx-auto w-full max-w-4xl'}>
          {playful ? (
            // Topo no padrão da tela de Conquistas: T.I.A estourando o card + balão explicando a tela.
            <div
              className="relative mb-6 mt-16 flex items-center rounded-[24px] p-4 pl-[160px] shadow-card"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <img
                src={tiaTrilhaEscolha}
                alt="T.I.A"
                className="pointer-events-none absolute bottom-0 left-2 z-10 h-[190px] w-auto object-contain object-bottom"
              />
              <div className="relative z-10 max-w-[800px] rounded-[16px] rounded-bl-none bg-white p-4 shadow-card">
                <p
                  className="mb-0 text-sm font-bold leading-relaxed"
                  style={{ color: '#222731' }}
                >
                  <TypewriterText text={'Esta é a sua Trilha! 🗺️ Escolha um caminho e avance bloco a bloco, no seu ritmo — cada bloco completado te deixa mais perto da sua meta. Bora começar?'} />
                </p>
              </div>
            </div>
          ) : (
            <header className="mb-7">
              <h1 className="text-2xl font-bold text-neutral-900">Escolha sua trilha</h1>
              <p className="mt-1 text-[15px] text-neutral-500">Um caminho montado pra você evoluir no seu ritmo, vencendo bloco a bloco.</p>
            </header>
          )}

          {(() => {
            const active = paths.filter(p => p['user-status'] === 'active');
            const available = paths.filter(p => p['user-status'] !== 'active');

            const renderCards = (list, indexOffset = 0) =>
              list.map((path, i) => (
                <TrilhaCard
                  key={path.id}
                  path={path}
                  index={indexOffset + i}
                  starting={starting}
                  onStart={start}
                  onContinue={() => navigate(PATHS.LEARNING_PATH.CURRENT_BY_ID(path['user-learning-path-id']))}
                />
              ));

            // Categorias como colunas, no mesmo apelo visual da tela de
            // Conquistas (MetricSection + MetricHat): "Em andamento" âmbar
            // (CTA laranja do card ativo), "Disponíveis" lilás (botões roxos).
            if (playful) {
              const sections = [
                active.length > 0 && { key: 'active', hat: 'Em andamento', variant: 'mission', cards: renderCards(active) },
                available.length > 0 && { key: 'available', hat: 'Disponíveis', variant: 'conquest', cards: renderCards(available, active.length) }
              ].filter(Boolean);

              return (
                <div className={`grid grid-cols-1 gap-4 lg:items-start ${sections.length > 1 ? 'lg:grid-cols-2' : ''}`}>
                  {sections.map(section => (
                    <MetricSection
                      key={section.key}
                      variant={section.variant}
                      className="rounded-[20px] border bg-white shadow-card"
                    >
                      <MetricHat>{section.hat}</MetricHat>
                      <div className="grid gap-4">{section.cards}</div>
                    </MetricSection>
                  ))}
                </div>
              );
            }

            const gridClass = 'grid gap-4 sm:grid-cols-2';
            const headingClass = 'mb-3 text-sm font-bold text-neutral-500';
            return (
              <>
                {active.length > 0 && (
                  <section>
                    <h2 className={headingClass}>Em andamento</h2>
                    <div className={gridClass}>{renderCards(active)}</div>
                  </section>
                )}
                {available.length > 0 && (
                  <section className={active.length > 0 ? 'mt-8' : ''}>
                    <h2 className={headingClass}>Disponíveis</h2>
                    <div className={gridClass}>{renderCards(available, active.length)}</div>
                  </section>
                )}
              </>
            );
          })()}
        </div>
      )}
    </DashboardContent>
  );
}
